import { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const menuRef = useRef(null);

  const LINKS = useMemo(
    () => [
      { to: "/", label: "Acasă", end: true },
      { to: "/rochii/mireasa", label: "Rochii de mireasă" },
      { to: "/rochii/cununie-civila", label: "Cununie civilă" },
      { to: "/rochii/domnisoara-de-onoare", label: "Domnișoară de onoare" },
      { to: "/rochii/cocktail", label: "Cocktail" },
      { to: "/rochii/seara", label: "Rochii de seară" },
      { to: "/rochii/ocazie", label: "Ocazie" },
      { to: "/rochii/revelion", label: "Revelion" },
      { to: "/portofoliu", label: "Portofoliu" },
      { to: "/contact", label: "Contact" },
    ],
    []
  );

  // Close menu on route change
  useEffect(() => setOpen(false), [location.pathname]);

  // Lock body scroll + ESC + simple focus trap
  useEffect(() => {
    const prev = document.body.style.overflow || "";
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = prev;

    const onKey = (e) => {
      if (!open) return;
      if (e.key === "Escape") setOpen(false);
      if (e.key === "Tab" && menuRef.current) {
        const focusables = menuRef.current.querySelectorAll(
          'a, button, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          last.focus(); e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus(); e.preventDefault();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Close mobile menu if we resize to desktop
  useEffect(() => {
    const onResize = () => {
      if (window.matchMedia("(min-width: 768px)").matches) setOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const linkBase = "px-3 py-2 rounded-md hover:text-gray-800 hover:bg-gray-50 transition";
  const active = "text-gray-900 font-medium underline underline-offset-4";

  return (
    <header
      className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b"
      style={{ paddingTop: "env(safe-area-inset-top)" }}
    >
      {/* MOBILE TOP BAR */}
      <div className="md:hidden">
        <div className="relative h-16 px-4 flex items-center justify-center">
          <Link to="/" aria-label="TytyStyle" className="inline-flex items-center">
            <img src="/logo.png" alt="TytyStyle" className="h-12 w-auto select-none" draggable="false" />
          </Link>

          <button
            type="button"
            aria-label={open ? "Închide meniul" : "Deschide meniul"}
            className="absolute right-4 top-1/2 -translate-y-1/2 inline-flex items-center justify-center rounded-md p-3 hover:bg-gray-100"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? (
              <svg width="24" height="24" viewBox="0 0 24 24">
                <path d="M6 6l12 12M6 18L18 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24">
                <path d="M3 6h18M3 12h18M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* DESKTOP (logo centrat + bară linkuri sub el) */}
      <div className="hidden md:block">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-center">
          <Link to="/" aria-label="TytyStyle" className="inline-flex items-center">
            <img src="/logo.png" alt="TytyStyle" className="h-16 lg:h-20 w-auto select-none" draggable="false" />
          </Link>
        </div>
        <nav className="w-full border-t">
          <div className="max-w-6xl mx-auto px-4">
            <div className="flex flex-wrap items-center justify-center gap-2 py-3 text-sm">
              {LINKS.map(({ to, label, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  className={({ isActive }) => `${linkBase} ${isActive ? active : ""}`}
                >
                  {label}
                </NavLink>
              ))}
            </div>
          </div>
        </nav>
      </div>

      {/* MOBILE FULLSCREEN MENU via PORTAL */}
      {open &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] bg-white flex flex-col"
            role="dialog"
            aria-modal="true"
            style={{
              paddingTop: "env(safe-area-inset-top)",
              paddingBottom: "env(safe-area-inset-bottom)",
            }}
          >
            {/* top bar */}
            <div className="h-16 px-4 border-b flex items-center justify-center relative">
              <img src="/logo.png" alt="TytyStyle" className="h-10 w-auto" />
              <button
                aria-label="Închide"
                onClick={() => setOpen(false)}
                className="absolute right-4 inline-flex items-center justify-center rounded-full p-2 hover:bg-gray-100"
              >
                <svg width="22" height="22" viewBox="0 0 24 24">
                  <path d="M6 6l12 12M6 18L18 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            {/* links (scrollable) */}
            <div ref={menuRef} className="flex-1 overflow-y-auto px-4 py-4">
              <nav className="grid grid-cols-1 gap-3">
                {LINKS.map(({ to, label, end }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={end}
                    className={({ isActive }) =>
                      `w-full text-center rounded-xl border px-4 py-4 text-base active:scale-[0.98] transition ${
                        isActive
                          ? "border-black text-black bg-white font-medium"
                          : "border-gray-200 text-gray-900 bg-gray-100"
                      }`
                    }
                  >
                    {label}
                  </NavLink>
                ))}
              </nav>
            </div>
          </div>,
          document.body
        )}
    </header>
  );
}
